import pLimit from 'p-limit';
import { generateContextImage, isFalConfigured } from '../../services/falImageService';
import { resolveWikimediaImageUrl } from '../../services/wikimediaImageService';
import type { ContextImageFields } from '../types/payloads/contextImage.types';
import { pickSanitizedImagePrompt, findPrimaryContextBlockIndex } from './tangibleImageEnforcer';
import { ensureMandatorySubjectImagePrompts } from './mandatoryContextImageEnforcer';
import { ensureVisualPacemakerImagePrompts } from './visualPacemakerEnforcer';
import { normalizeWikiQuery } from './wikiQueryNormalizer';

export interface BlockWithPayload {
  mode: string;
  payload?: Record<string, unknown>;
  label?: string;
  index?: number;
}

const CONTEXT_IMAGE_SUBJECTS = new Set([
  'lenguaje',
  'historia',
  'ciencias',
  'ciencia',
  'language',
  'history',
  'science',
]);

const IMAGE_PROMPT_MODES = new Set([
  'info_block',
  'desarrollo',
  'completar',
  'arena',
  'vincula',
  'agrupa',
  'order',
  'destacado',
  'figura',
]);

const WIKI_QUERY_MODES = new Set(['figura', 'info_block', 'destacado']);

/** Máximo 2 llamadas concurrentes a Fal.ai para no saturar la API en guías largas. */
const falConcurrencyLimit = pLimit(2);

export function shouldGenerateContextImages(
  subjectTag?: string,
  options: { isGuide?: boolean } = {},
): boolean {
  const tag = String(subjectTag || '').toLowerCase();
  if (CONTEXT_IMAGE_SUBJECTS.has(tag)) return true;
  if (tag.includes('cienc') || tag.includes('lengua') || tag.includes('histor')) return true;
  if (options.isGuide && tag.includes('matemat')) return true;
  return false;
}

function truncateWords(text: string, maxWords: number): string {
  return String(text || '')
    .trim()
    .split(/\s+/)
    .slice(0, maxWords)
    .join(' ');
}

/** Si Mistral omitió imagePrompt, infiere uno corto desde estímulo narrativo tangible. */
export function inferImagePromptFallback(
  payload: Record<string, unknown>,
  blockMode: string,
): string | undefined {
  if (resolveImagePrompt(payload)) return undefined;

  const stimulus =
    payload.stimulus && typeof payload.stimulus === 'object'
      ? (payload.stimulus as Record<string, unknown>)
      : null;

  const narrativeTypes = new Set([
    'news',
    'historical_source',
    'case_study',
    'poem',
    'prose',
    'scientific_data',
  ]);

  if (stimulus?.content && String(stimulus.content).trim().length >= 24) {
    const type = String(stimulus.type || 'general');
    if (!narrativeTypes.has(type)) return undefined;
    const hint = String(stimulus.title || payload.title || payload.printTitle || '').trim();
    if (hint.length >= 3) return truncateWords(hint, 15);
  }

  return undefined;
}

export function resolveImagePrompt(payload: Record<string, unknown>): string | undefined {
  if (String(payload.wikiQuery || '').trim()) return undefined;

  const direct = String(payload.imagePrompt || '').trim();
  if (direct) return direct;

  const stimulus = payload.stimulus;
  if (stimulus && typeof stimulus === 'object') {
    const stim = stimulus as ContextImageFields;
    if (String(stim.wikiQuery || '').trim()) return undefined;
    const fromStimulus = String(stim.imagePrompt || '').trim();
    if (fromStimulus) return fromStimulus;
  }

  return undefined;
}

export function resolveWikiQuery(payload: Record<string, unknown>): string | undefined {
  const topicHint = String(payload.title || payload.sectionTitle || '').trim();
  const direct = normalizeWikiQuery(String(payload.wikiQuery || '').trim(), topicHint);
  if (direct) return direct;

  const stimulus = payload.stimulus;
  if (stimulus && typeof stimulus === 'object') {
    const fromStimulus = normalizeWikiQuery(
      String((stimulus as ContextImageFields).wikiQuery || '').trim(),
      topicHint,
    );
    if (fromStimulus) return fromStimulus;
  }

  return undefined;
}

export function resolveImageUrl(payload: Record<string, unknown>): string | undefined {
  const direct = String(payload.imageUrl || '').trim();
  if (direct) return direct;

  const stimulus = payload.stimulus;
  if (stimulus && typeof stimulus === 'object') {
    const fromStimulus = String((stimulus as ContextImageFields).imageUrl || '').trim();
    if (fromStimulus) return fromStimulus;
  }

  return undefined;
}

function assignImageUrl(payload: Record<string, unknown>, imageUrl: string): Record<string, unknown> {
  const next: Record<string, unknown> = { ...payload, imageUrl };

  if (payload.stimulus && typeof payload.stimulus === 'object') {
    next.stimulus = { ...(payload.stimulus as Record<string, unknown>), imageUrl };
  }

  return next;
}

/**
 * Para cada bloque con imagePrompt y sin imageUrl, genera ilustración vía fal.ai.
 * Falla de forma aislada por bloque — no tumba la guía completa.
 */
export async function enrichBlocksWithContextImages<T extends BlockWithPayload>(
  blocks: T[],
  subjectTag?: string,
  topic = '',
  options: {
    isStudyGuide?: boolean;
    isGuide?: boolean;
    /** Wave-2 #5 (A): con composer activo, no inventar imagePrompt paralelo al slot. */
    skipInventedImagePrompts?: boolean;
  } = {},
): Promise<T[]> {
  if (!shouldGenerateContextImages(subjectTag, { isGuide: options.isGuide ?? true })) {
    return blocks;
  }

  let prepared = blocks;
  if (isFalConfigured() && !options.skipInventedImagePrompts) {
    prepared = ensureMandatorySubjectImagePrompts(blocks, subjectTag, topic);
    prepared = ensureVisualPacemakerImagePrompts(prepared, subjectTag, {
      isStudyGuide: options.isStudyGuide,
      topic,
    });
  } else if (options.skipInventedImagePrompts) {
    console.info(
      '[SALA] skipInventedImagePrompts: solo respeta imagePrompt/wikiQuery ya presentes en el slot',
    );
  }

  const primaryContextIndex = findPrimaryContextBlockIndex(prepared);
  const imageOptions = { subjectTag, primaryContextIndex };

  const enriched = await Promise.all(
    prepared.map(async (block, index) => {
      const payload = (block.payload ?? {}) as Record<string, unknown>;
      const wikiQuery = WIKI_QUERY_MODES.has(block.mode) ? resolveWikiQuery(payload) : undefined;

      if (wikiQuery && !resolveImageUrl(payload)) {
        try {
          const imageUrl = await resolveWikimediaImageUrl(wikiQuery);
          if (imageUrl) {
            console.info(`[context-image] wikimedia mode=${block.mode} query="${wikiQuery.slice(0, 40)}…"`);
            return {
              ...block,
              payload: assignImageUrl({ ...payload, wikiQuery }, imageUrl),
            };
          }
        } catch (error) {
          console.warn(
            `[context-image] wikimedia skip mode=${block.mode}:`,
            error instanceof Error ? error.message : error,
          );
        }
      }

      if (!IMAGE_PROMPT_MODES.has(block.mode) || !isFalConfigured()) return block;

      // Con composer: no inferir prompt desde body genérico — solo prompts ya anclados al slot.
      const fallback = options.skipInventedImagePrompts
        ? ''
        : inferImagePromptFallback(payload, block.mode);
      const imagePrompt = pickSanitizedImagePrompt(
        block,
        index,
        prepared.length,
        payload,
        fallback,
        imageOptions,
      );
      if (!imagePrompt || resolveImageUrl(payload)) return block;

      try {
        const imageUrl = await falConcurrencyLimit(() =>
          generateContextImage(imagePrompt, { retries: 1 }),
        );
        console.info(`[context-image] fal mode=${block.mode} prompt="${truncateWords(imagePrompt, 6)}…" url=${imageUrl.slice(0, 48)}…`);
        return {
          ...block,
          payload: assignImageUrl(payload, imageUrl),
        };
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        console.error(
          `[context-image] FAL falló mode=${block.mode} index=${index} prompt="${truncateWords(imagePrompt, 8)}…": ${message}`,
        );
        return block;
      }
    }),
  );

  return enriched;
}
